import { useState, useEffect, useCallback } from 'react'
import { Trash2, AlertCircle, Loader2, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface InactiveVersion {
  seed: string
  version: string
  workerName: string | null
  isCurrent: boolean
  deployed: boolean
}

interface GuidePublicationProblem {
  seed: string
  seedVersion: string
  revision: number
  issue: 'missing-objects' | 'objects-restored' | 'expired-lease'
  recovery: 'mark-broken' | 'mark-active' | 'finish-prune' | 'release-lease'
}

interface DispatchCategory {
  skipped?: boolean
  reason?: string
  scripts?: string[]
}

interface DeletedReport {
  r2Seeds: number
  r2Shares: number
  r2Guides: number
  guidePublications: number
  dispatch: number
  authPins: number
}

type Status<T> =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'ok'; value: T }
  | { state: 'error'; message: string }

/**
 * Fetch with timeout that surfaces a clear error instead of letting the
 * spinner hang. 20s default — Cloudflare workers cap responses well below
 * that on idle paths, so a real-world timeout means something's wrong.
 */
async function fetchJsonWithTimeout<T>(url: string, ms = 20000): Promise<T> {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), ms)
  try {
    const res = await fetch(url, { method: 'POST', signal: ctrl.signal })
    if (!res.ok) {
      const body = await res.text().catch(() => '')
      throw new Error(`HTTP ${res.status}${body ? ` — ${body.slice(0, 200)}` : ''}`)
    }
    return (await res.json()) as T
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error(`Timed out after ${ms / 1000}s`)
    }
    throw err
  } finally {
    clearTimeout(timer)
  }
}

export function CleanupPage() {
  const [r2Seeds, setR2Seeds] = useState<Status<string[]>>({ state: 'idle' })
  const [r2Shares, setR2Shares] = useState<Status<string[]>>({ state: 'idle' })
  const [r2Guides, setR2Guides] = useState<Status<string[]>>({ state: 'idle' })
  const [guidePublications, setGuidePublications] = useState<Status<GuidePublicationProblem[]>>({ state: 'idle' })
  const [dispatch, setDispatch] = useState<Status<DispatchCategory>>({ state: 'idle' })
  const [authPins, setAuthPins] = useState<Status<number>>({ state: 'idle' })
  const [inactive, setInactive] = useState<Status<InactiveVersion[]>>({ state: 'idle' })

  const [applying, setApplying] = useState(false)
  const [applyError, setApplyError] = useState<string | null>(null)
  const [lastDeleted, setLastDeleted] = useState<DeletedReport | null>(null)

  const scanR2Seeds = useCallback(async () => {
    setR2Seeds({ state: 'loading' })
    try {
      const { r2Seeds } = await fetchJsonWithTimeout<{ r2Seeds: string[] }>('/api/admin/cleanup/r2-seeds')
      setR2Seeds({ state: 'ok', value: r2Seeds })
    } catch (err) {
      setR2Seeds({ state: 'error', message: err instanceof Error ? err.message : String(err) })
    }
  }, [])

  const scanR2Shares = useCallback(async () => {
    setR2Shares({ state: 'loading' })
    try {
      const { r2Shares } = await fetchJsonWithTimeout<{ r2Shares: string[] }>('/api/admin/cleanup/r2-shares')
      setR2Shares({ state: 'ok', value: r2Shares })
    } catch (err) {
      setR2Shares({ state: 'error', message: err instanceof Error ? err.message : String(err) })
    }
  }, [])

  const scanR2Guides = useCallback(async () => {
    setR2Guides({ state: 'loading' })
    try {
      const { r2Guides } = await fetchJsonWithTimeout<{ r2Guides: string[] }>('/api/admin/cleanup/r2-guides')
      setR2Guides({ state: 'ok', value: r2Guides })
    } catch (err) {
      setR2Guides({ state: 'error', message: err instanceof Error ? err.message : String(err) })
    }
  }, [])

  const scanGuidePublications = useCallback(async () => {
    setGuidePublications({ state: 'loading' })
    try {
      const body = await fetchJsonWithTimeout<{ guidePublications: GuidePublicationProblem[] }>('/api/admin/cleanup/guide-publications')
      setGuidePublications({ state: 'ok', value: body.guidePublications })
    } catch (err) {
      setGuidePublications({ state: 'error', message: err instanceof Error ? err.message : String(err) })
    }
  }, [])

  const scanDispatch = useCallback(async () => {
    setDispatch({ state: 'loading' })
    try {
      const { dispatch } = await fetchJsonWithTimeout<{ dispatch: DispatchCategory }>('/api/admin/cleanup/dispatch')
      setDispatch({ state: 'ok', value: dispatch })
    } catch (err) {
      setDispatch({ state: 'error', message: err instanceof Error ? err.message : String(err) })
    }
  }, [])

  const scanAuthPins = useCallback(async () => {
    setAuthPins({ state: 'loading' })
    try {
      const { authPins } = await fetchJsonWithTimeout<{ authPins: number }>('/api/admin/cleanup/auth-pins')
      setAuthPins({ state: 'ok', value: authPins })
    } catch (err) {
      setAuthPins({ state: 'error', message: err instanceof Error ? err.message : String(err) })
    }
  }, [])

  const scanInactive = useCallback(async () => {
    setInactive({ state: 'loading' })
    try {
      const { inactiveVersions } = await fetchJsonWithTimeout<{ inactiveVersions: InactiveVersion[] }>(
        '/api/admin/cleanup/inactive-versions',
        30000,
      )
      setInactive({ state: 'ok', value: inactiveVersions })
    } catch (err) {
      setInactive({ state: 'error', message: err instanceof Error ? err.message : String(err) })
    }
  }, [])

  const rescanAll = useCallback(() => {
    scanR2Seeds()
    scanR2Shares()
    scanR2Guides()
    scanGuidePublications()
    scanDispatch()
    scanAuthPins()
    // Inactive is opt-in — leave it alone.
  }, [scanR2Seeds, scanR2Shares, scanR2Guides, scanGuidePublications, scanDispatch, scanAuthPins])

  useEffect(() => { rescanAll() }, [rescanAll])

  const totalOrphans =
    (r2Seeds.state === 'ok' ? r2Seeds.value.length : 0) +
    (r2Shares.state === 'ok' ? r2Shares.value.length : 0) +
    (r2Guides.state === 'ok' ? r2Guides.value.length : 0) +
    (guidePublications.state === 'ok' ? guidePublications.value.length : 0) +
    (dispatch.state === 'ok' ? dispatch.value.scripts?.length ?? 0 : 0) +
    (authPins.state === 'ok' ? authPins.value : 0)

  async function applyCleanup() {
    if (!confirm(`Delete ${totalOrphans} orphan(s)? This is irreversible.`)) return
    setApplying(true)
    setApplyError(null)
    try {
      const res = await fetch('/api/admin/cleanup?apply=true', { method: 'POST' })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || `HTTP ${res.status}`)
      }
      const body = await res.json() as { deleted?: DeletedReport }
      setLastDeleted(body.deleted ?? null)
      rescanAll()
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : String(err))
    } finally {
      setApplying(false)
    }
  }

  return (
    <div className="mx-auto max-w-5xl p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Orphan cleanup</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Reconciles R2 storage and the dispatch namespace against D1. Each category scans independently — if one is slow, the others still load.
        </p>
      </div>

      {applyError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{applyError}</AlertDescription>
        </Alert>
      )}

      {lastDeleted && (
        <Alert>
          <AlertDescription>
            Cleanup applied — deleted {lastDeleted.r2Seeds} seed R2 object{lastDeleted.r2Seeds === 1 ? '' : 's'},
            {' '}{lastDeleted.r2Shares} share R2 object{lastDeleted.r2Shares === 1 ? '' : 's'},
            {' '}{lastDeleted.r2Guides} guide R2 object{lastDeleted.r2Guides === 1 ? '' : 's'},
            {' '}{lastDeleted.guidePublications} guide publication state{lastDeleted.guidePublications === 1 ? '' : 's'} reconciled,
            {' '}{lastDeleted.dispatch} dispatch script{lastDeleted.dispatch === 1 ? '' : 's'},
            {' '}{lastDeleted.authPins} stale auth pin{lastDeleted.authPins === 1 ? '' : 's'}.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={rescanAll}>
          <RefreshCw className="size-4 mr-1" />
          Re-scan all
        </Button>
        <Button
          variant="destructive"
          onClick={applyCleanup}
          disabled={applying || totalOrphans === 0}
        >
          {applying ? <Loader2 className="size-4 animate-spin mr-1" /> : <Trash2 className="size-4 mr-1" />}
          Apply cleanup ({totalOrphans})
        </Button>
      </div>

      <div className="space-y-4">
        <CategoryCard
          title="Guide publication health"
          description="Active D1 publications with missing R2 objects or an expired approval/prune lease. Apply cleanup records a safe tombstone, unavailable state, or releases the stale lease."
          status={guidePublications}
          onRescan={scanGuidePublications}
          render={(items) => items.length === 0 ? <p className="text-sm text-muted-foreground">All guide publications are consistent.</p> : <ul className="space-y-1 text-xs">{items.map(item => <li key={`${item.seed}/${item.seedVersion}/${item.revision}`} className="flex flex-wrap gap-2"><a href={`/guides/${item.seed}`} className="font-mono text-foreground hover:text-primary">{item.seed}</a><span className="font-mono text-muted-foreground">v{item.seedVersion} · r{item.revision}</span><span className="text-amber-700 dark:text-amber-300">{item.issue === 'missing-objects' ? 'Missing objects' : item.issue === 'objects-restored' ? 'Objects restored' : 'Expired operation lease'}</span></li>)}</ul>}
        />
        <CategoryCard
          title="R2 guide orphans"
          description="seeds/{seed}/{version}/{revision}/ prefixes in the guide bucket with no publication row."
          status={r2Guides}
          onRescan={scanR2Guides}
          render={(items) => <ListItems items={items} />}
        />
        <CategoryCard
          title="R2 seed orphans"
          description="seeds/{seed}/{version}/ prefixes with no matching seed_versions row."
          status={r2Seeds}
          onRescan={scanR2Seeds}
          render={(items) => <ListItems items={items} />}
        />
        <CategoryCard
          title="R2 share orphans"
          description="shares/{id}/ prefixes with no matching shares row."
          status={r2Shares}
          onRescan={scanR2Shares}
          render={(items) => <ListItems items={items} />}
        />
        <CategoryCard
          title="Dispatch orphans"
          description="Worker scripts in the dispatch namespace not referenced by any seed_versions.worker_name."
          status={dispatch}
          onRescan={scanDispatch}
          render={(d) =>
            d.skipped ? (
              <p className="text-xs text-muted-foreground italic">Skipped — {d.reason || 'CF API not configured'}</p>
            ) : !d.scripts || d.scripts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No orphans.</p>
            ) : (
              <ListItems items={d.scripts} />
            )
          }
        />
        <CategoryCard
          title="Auth pins"
          description="Pin rows for emails that no longer exist in clients."
          status={authPins}
          onRescan={scanAuthPins}
          render={(n) =>
            n === 0
              ? <p className="text-sm text-muted-foreground">No orphans.</p>
              : <p className="text-sm text-muted-foreground">{n} stale pin{n === 1 ? '' : 's'}.</p>
          }
        />
        <CategoryCard
          title="Inactive versions"
          description="Versions with no shares pinned or touched. Each holds a dispatch worker + any leaked DOs. Use per-seed Prune to clean."
          status={inactive}
          onRescan={scanInactive}
          rescanLabel={inactive.state === 'idle' ? 'Scan' : 'Re-scan'}
          render={(items) =>
            items.length === 0 ? (
              <p className="text-sm text-muted-foreground">None — every version is referenced by at least one share.</p>
            ) : (
              <ul className="space-y-1 text-xs">
                {items.map(v => (
                  <li key={`${v.seed}/${v.version}`} className="flex items-center gap-2">
                    <a href={`/seeds/${v.seed}`} className="font-mono text-foreground hover:text-primary">{v.seed}</a>
                    <span className="font-mono text-muted-foreground">v{v.version}</span>
                    {v.workerName && <span className="text-muted-foreground/60">→ {v.workerName}</span>}
                  </li>
                ))}
              </ul>
            )
          }
        />
      </div>
    </div>
  )
}

function CategoryCard<T>({
  title,
  description,
  status,
  onRescan,
  rescanLabel = 'Re-scan',
  render,
}: {
  title: string
  description: string
  status: Status<T>
  onRescan: () => void
  rescanLabel?: string
  render: (value: T) => React.ReactNode
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">{title}</CardTitle>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-7"
            disabled={status.state === 'loading'}
            onClick={onRescan}
          >
            {status.state === 'loading' ? <Loader2 className="size-3.5 animate-spin mr-1" /> : null}
            {rescanLabel}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {status.state === 'idle' ? (
          <p className="text-sm text-muted-foreground">Click <strong>{rescanLabel}</strong> to scan.</p>
        ) : status.state === 'loading' ? (
          <p className="text-sm text-muted-foreground">Scanning…</p>
        ) : status.state === 'error' ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="font-mono text-xs whitespace-pre-wrap break-all">
              {status.message}
            </AlertDescription>
          </Alert>
        ) : (
          render(status.value)
        )}
      </CardContent>
    </Card>
  )
}

function ListItems({ items }: { items: string[] }) {
  if (items.length === 0) return <p className="text-sm text-muted-foreground">No orphans.</p>
  return (
    <ul className="space-y-1">
      {items.map(p => <li key={p} className="font-mono text-xs text-foreground">{p}</li>)}
    </ul>
  )
}
